/**
 * Customer Role Class
 * Implements regular user/customer behaviors and permissions
 */

import { BaseRole, NavigationItem, QuickAction, DashboardMetric } from './BaseRole';

export class CustomerRole extends BaseRole {
  get roleName(): string {
    return 'USER';
  }

  get displayName(): string {
    return 'Customer';
  }

  getDefaultRoute(): string {
    return '/';
  }

  getNavigationItems(): NavigationItem[] {
    const items: NavigationItem[] = [];
    
    items.push({
      label: 'Home',
      path: '/',
      icon: 'Home'
    });
    
    items.push({
      label: 'Marketplace',
      path: '/marketplace',
      icon: 'ShoppingBag'
    });
    
    if (!this.user.isGuest) {
      items.push({
        label: 'Wallet',
        path: '/wallet',
        icon: 'Wallet',
        children: [
          { label: 'Balances', path: '/balances' },
          { label: 'Send Money', path: '/send' },
          { label: 'Request Money', path: '/request' },
          { label: 'Top Up', path: '/top-up' }
        ]
      });
      
      items.push({
        label: 'Transactions',
        path: '/transactions',
        icon: 'Receipt'
      });
      
      items.push({
        label: 'Contacts',
        path: '/contacts',
        icon: 'Users'
      });
      
      items.push({
        label: 'Profile',
        path: '/profile',
        icon: 'User',
        children: [
          { label: 'Personal Info', path: '/profile' },
          { label: 'Security', path: '/profile/security' },
          { label: 'Preferences', path: '/profile/preferences' },
          { label: 'Payment Methods', path: '/profile/payment-methods' }
        ]
      });
    }
    
    items.push({
      label: 'Help',
      path: '/help',
      icon: 'HelpCircle'
    });
    
    return items;
  }

  canPerformAction(action: string): boolean {
    const isGuest = this.user.isGuest;
    
    const permissions: Record<string, boolean> = {
      'view_marketplace': true,
      'view_merchant_profile': true,
      'make_guest_payment': true,
      'view_help': true,
      'view_balances': !isGuest,
      'send_money': !isGuest,
      'request_money': !isGuest,
      'create_token': !isGuest,
      'redeem_token': !isGuest,
      'top_up_balance': !isGuest,
      'transfer_out': !isGuest,
      'view_transactions': !isGuest,
      'manage_contacts': !isGuest,
      'update_profile': !isGuest,
      'manage_payment_methods': !isGuest,
      'follow_merchant': !isGuest,
      'leave_review': !isGuest,
      'save_favorites': !isGuest,
      'export_data': !isGuest,
      'delete_account': !isGuest,
      'enable_2fa': !isGuest,
      'manage_notifications': !isGuest
    };
    
    return permissions[action] || false;
  }

  getBadgeColor(): string {
    if (this.user.isGuest) return 'secondary';
    if (this.user.isProfileComplete) return 'success';
    return 'default';
  }

  getIconName(): string {
    return this.user.isGuest ? 'UserX' : 'User';
  }

  getWelcomeMessage(): string {
    if (this.user.isGuest) {
      return `Welcome, Guest! Sign up to unlock all features.`;
    }
    return `Welcome back, ${this.user.displayName}!`;
  }

  getQuickActions(): QuickAction[] {
    const actions: QuickAction[] = [];
    
    if (this.user.isGuest) {
      actions.push({
        id: 'sign_up',
        title: 'Create Account',
        description: 'Sign up to access all features',
        icon: 'UserPlus',
        action: '/signup',
        variant: 'primary'
      });
      
      actions.push({
        id: 'browse_marketplace',
        title: 'Browse Marketplace',
        description: 'Explore merchants and products',
        icon: 'ShoppingBag',
        action: '/marketplace'
      });
      
      return actions;
    }
    
    // Regular user actions
    actions.push({
      id: 'send_money',
      title: 'Send Money',
      description: 'Transfer funds to another user',
      icon: 'Send',
      action: '/send'
    });
    
    actions.push({
      id: 'top_up',
      title: 'Add Funds',
      description: 'Top up your wallet balance',
      icon: 'Plus',
      action: '/top-up'
    });
    
    actions.push({
      id: 'scan_qr',
      title: 'Scan QR Code',
      description: 'Scan to pay or redeem',
      icon: 'QrCode',
      action: () => 'open_qr_scanner'
    });
    
    actions.push({
      id: 'marketplace',
      title: 'Shop',
      description: 'Browse the marketplace',
      icon: 'ShoppingBag',
      action: '/marketplace'
    });
    
    if (!this.user.isProfileComplete) {
      actions.push({
        id: 'complete_profile',
        title: 'Complete Profile',
        description: 'Finish setting up your profile',
        icon: 'User',
        action: '/profile',
        variant: 'secondary'
      });
    }
    
    return actions;
  }

  getDashboardMetrics(): DashboardMetric[] {
    const metrics: DashboardMetric[] = [];
    
    if (this.user.isGuest) return metrics;
    
    // Total balance across all currencies
    metrics.push({
      id: 'total_balance',
      label: 'Total Balance',
      value: `$${this.user.totalBalance.toFixed(2)}`,
      icon: 'Wallet',
      color: 'green'
    });
    
    // Primary currency balance
    const primaryBalance = this.user.getBalance(this.user.primaryCurrency);
    metrics.push({
      id: 'primary_balance',
      label: this.user.primaryCurrency,
      value: primaryBalance.toFixed(2),
      icon: 'DollarSign',
      color: 'blue'
    });
    
    // Number of currencies
    metrics.push({
      id: 'currencies',
      label: 'Currencies',
      value: this.user.getCurrencies().length,
      icon: 'Globe',
      color: 'purple'
    });
    
    // Profile completion
    metrics.push({
      id: 'profile_completion',
      label: 'Profile',
      value: `${this.user.profileCompleteness}%`,
      icon: 'User',
      color: this.user.isProfileComplete ? 'green' : 'yellow'
    });
    
    return metrics;
  }

  // ===== Customer-specific helper methods =====

  canMakePurchase(amount: number, currency: string): boolean {
    if (this.user.isGuest) return true; // Guests can pay with external methods
    return this.user.hasBalance(currency, amount);
  }

  getPreferredPaymentMethod(): string {
    if (this.user.isGuest) return 'card';
    if (this.user.totalBalance > 0) return 'balance';
    return 'card';
  }

  getSuggestedActions(): string[] {
    const suggestions: string[] = [];
    
    if (this.user.isGuest) {
      suggestions.push('Create an account to save your data');
      return suggestions;
    }
    
    if (!this.user.isProfileComplete) {
      suggestions.push('Complete your profile for a better experience');
    }
    
    if (this.user.totalBalance === 0) {
      suggestions.push('Add funds to your wallet to start transacting');
    }
    
    if (!this.user.profile.phone) {
      suggestions.push('Add a phone number for account recovery');
    }
    
    if (this.user.missingProfileFields.length > 0) {
      suggestions.push(`Add your ${this.user.missingProfileFields[0].toLowerCase()}`);
    }
    
    return suggestions;
  }

  getUpgradeOptions(): Array<{ role: string; benefits: string[] }> {
    const options = [];
    
    if (!this.user.isMerchant) {
      options.push({
        role: 'MERCHANT',
        benefits: [
          'Accept payments from customers',
          'Create and manage product catalog',
          'Access business analytics',
          'Generate payment QR codes',
          'Send invoices'
        ]
      });
    }
    
    if (!this.user.isDeveloper) {
      options.push({
        role: 'DEVELOPER',
        benefits: [
          'Access API documentation',
          'Generate API keys',
          'Build integrations',
          'Access webhooks',
          'Test in sandbox environment'
        ]
      });
    }
    
    return options;
  }
}
